import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@groot/shell/lib/api";
import type { PrintBatchDocument, PrintFormat } from "../../../../shared/print";

export type PrintBatchStatus = "draft" | "printed" | "photos_cleared";
export type PreviewFeedback = "pending" | "loved" | "change_requested";

export interface PreviewSummary {
  version: number;
  loved: number;
  changes: number;
  pending: number;
  total: number;
  /** When the customer last swiped on this preview (null: not yet). */
  lastRespondedAt: string | null;
}

export interface PrintBatchSourceSummary {
  photoRequestId: number;
  orderId: number;
  orderNumber: string;
  customerName: string;
  customerMobile: string | null;
  previewLink: string | null;
  previewSharedAt: string | null;
  previewSubmittedAt: string | null;
  photosClearedAt: string | null;
  preview: PreviewSummary | null;
}

export interface PrintBatchListItem {
  id: number;
  name: string;
  status: PrintBatchStatus;
  pdfGeneratedAt: string | null;
  printedAt: string | null;
  createdAt: string;
  updatedAt: string;
  sources: (PrintBatchSourceSummary | null)[];
  _count: { pages: number; photos: number };
}

export interface SourceFile {
  id: number;
  fileName: string;
  width: number | null;
  height: number | null;
  focusX: number | null;
  focusY: number | null;
}

export interface PrintBatchSource extends PrintBatchSourceSummary {
  ordered: { format: PrintFormat; quantity: number }[];
  files: SourceFile[];
}

export interface FrameFeedback {
  photoRequestId: number;
  feedback: PreviewFeedback;
  reasons: string[];
  note: string | null;
  focusHint: { x: number; y: number } | null;
  respondedAt: string | null;
  isUpdated: boolean;
}

export interface PrintBatch {
  id: number;
  name: string;
  status: PrintBatchStatus;
  version: number;
  document: PrintBatchDocument;
  pdfKey: string | null;
  pdfGeneratedAt: string | null;
  printedAt: string | null;
  createdAt: string;
  updatedAt: string;
  sources: PrintBatchSource[];
  feedback: Record<string, FrameFeedback>;
}

export interface ShareResult {
  jobId: string;
  jobName: string;
  links: {
    photoRequestId: number;
    previewLink: string;
    orderNumber: string;
    customerName: string;
    customerMobile: string | null;
  }[];
}

export const CHANGE_REASON_LABELS: Record<string, string> = {
  crop: "Crop differently",
  too_dark: "Too dark",
  too_bright: "Too bright",
  colours: "Colours look off",
  remove: "Remove this photo",
  swap: "Swap for another photo",
  other: "Something else",
};

/** The admin thumbnail of a source photo (EXIF-oriented WebP). */
export function thumbnailUrl(photoRequestId: number, fileId: number, width = 600) {
  return `/api/v1/photo-requests/${photoRequestId}/files/${fileId}?w=${width}`;
}

export function previewUrl(previewLink: string) {
  return `${window.location.origin}/public/print-previews/${previewLink}`;
}

/** WhatsApp deep link with a prefilled message to the customer's number. */
export function whatsappUrl(mobile: string | null, name: string, link: string) {
  const digits = (mobile ?? "").replace(/\D/g, "");
  const number = digits.length === 10 ? `91${digits}` : digits;
  const text = `Hi ${name.split(" ")[0]} 👋 Your polaroids are ready for a sneak peek! Swipe through and tell us what you love: ${link}`;
  return `https://wa.me/${number}?text=${encodeURIComponent(text)}`;
}

export function usePrintBatches(photoRequestId?: number) {
  return useQuery({
    queryKey: ["print-batches", { photoRequestId }],
    queryFn: () =>
      apiClient.get<PrintBatchListItem[]>(
        "/print-batches",
        photoRequestId ? { photoRequestId } : undefined,
      ),
  });
}

export function usePrintBatch(id: number) {
  return useQuery({
    queryKey: ["print-batches", id],
    queryFn: () => apiClient.get<PrintBatch>(`/print-batches/${id}`),
    enabled: !!id,
    // The editor owns the document once loaded; refetches only refresh feedback.
    refetchOnWindowFocus: false,
  });
}

export function useCreatePrintBatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { photoRequestIds: number[]; format?: PrintFormat; count?: number }) =>
      apiClient.post<PrintBatch>("/print-batches", input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["print-batches"] }),
  });
}

export function useSaveDocument(id: number) {
  return useMutation({
    mutationFn: (input: { version: number; document: PrintBatchDocument }) =>
      apiClient.put<{ version: number; updatedAt: string }>(`/print-batches/${id}/document`, input),
  });
}

export function useUpdatePrintBatch(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name?: string;
      status?: "draft" | "printed";
      markOrdersPrinting?: boolean;
      completeRequests?: boolean;
    }) => apiClient.patch<PrintBatch>(`/print-batches/${id}`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["print-batches"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["photo-requests"] });
    },
  });
}

export function useDeletePrintBatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiClient.delete(`/print-batches/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["print-batches"] }),
  });
}

export function useAddSources(id: number) {
  return useMutation({
    mutationFn: (photoRequestIds: number[]) =>
      apiClient.post<PrintBatch>(`/print-batches/${id}/sources`, { photoRequestIds }),
  });
}

export function useExportPdf(id: number) {
  return useMutation({
    mutationFn: () =>
      apiClient.post<{ jobId: string; jobName: string }>(`/print-batches/${id}/export`),
  });
}

export function useSharePreview(id: number) {
  return useMutation({
    mutationFn: () => apiClient.post<ShareResult>(`/print-batches/${id}/share`),
  });
}

interface JobState {
  id: string;
  state: "created" | "active" | "completed" | "failed" | "cancelled";
  error: string | null;
  output: unknown;
}

/** Poll a background job until it finishes. */
export async function waitForJob(
  jobName: string,
  jobId: string,
  timeoutMs = 10 * 60_000,
): Promise<JobState> {
  const started = Date.now();
  for (;;) {
    const job = await apiClient.get<JobState>(`/jobs/${jobName}/${jobId}`);
    if (job.state === "completed") return job;
    if (job.state === "failed" || job.state === "cancelled") {
      throw new Error(job.error || `The job ${job.state}`);
    }
    if (Date.now() - started > timeoutMs) throw new Error("Timed out waiting for the job");
    await new Promise((resolve) => setTimeout(resolve, 1500));
  }
}
